import { useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Loader2, CheckCircle2, Sparkles, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { QuickCaptureMode } from "./QuickCaptureMode";

interface ProcessedRecord {
  id: string;
  document_number: string;
  seller_name: string;
  seller_address: string;
}

export function DickinsonCountyScraper() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documentNumber, setDocumentNumber] = useState("");
  const [documentType, setDocumentType] = useState("Land Contract");
  const [filingDate, setFilingDate] = useState("");
  const [notes, setNotes] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedRecords, setProcessedRecords] = useState<ProcessedRecord[]>([]);
  
  // Automated scraping state
  const [autoStartDate, setAutoStartDate] = useState("");
  const [autoEndDate, setAutoEndDate] = useState("");
  const [isAutoScraping, setIsAutoScraping] = useState(false);
  const [autoResults, setAutoResults] = useState<{ recordsFound: number; recordsInserted: number } | null>(null);

  const handleAutoScrape = async () => {
    if (!autoStartDate || !autoEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    setIsAutoScraping(true);
    setAutoResults(null);

    try {
      toast.info("Starting automated scraping with Firecrawl...");

      const { data, error } = await supabase.functions.invoke('scrape-dickinson-firecrawl', {
        body: {
          startDate: autoStartDate,
          endDate: autoEndDate
        }
      });

      if (error) throw error;

      if (data.success) {
        setAutoResults({
          recordsFound: data.recordsFound,
          recordsInserted: data.recordsInserted
        });
        toast.success(data.message || "Scraping completed successfully!");
      } else {
        toast.error(data.error || "Scraping failed");
      }
    } catch (error) {
      console.error("Auto scraping error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to scrape records");
    } finally {
      setIsAutoScraping(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    setIsProcessing(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target?.result as string;

        toast.info("Processing image with AI...");

        const { data, error } = await supabase.functions.invoke('extract-dickinson-contract', {
          body: {
            imageData: base64Data,
            metadata: {
              documentNumber: documentNumber || undefined,
              documentType,
              filingDate: filingDate || undefined,
              notes: notes || undefined
            }
          }
        });

        if (error) throw error;

        if (data.success) {
          toast.success("Contract extracted and saved successfully!");
          setProcessedRecords(prev => [...prev, {
            id: data.record.id,
            document_number: data.record.document_number,
            seller_name: data.record.grantor_name,
            seller_address: data.record.grantee_address
          }]);
          
          // Reset form
          setDocumentNumber("");
          setNotes("");
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        } else {
          toast.error(data.error || "Failed to process contract");
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Processing error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to process image");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="quick" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="quick">
            <Zap className="mr-2 h-4 w-4" />
            Quick Capture
          </TabsTrigger>
          <TabsTrigger value="automated">
            <Sparkles className="mr-2 h-4 w-4" />
            Automated
          </TabsTrigger>
          <TabsTrigger value="manual">
            <Upload className="mr-2 h-4 w-4" />
            Manual Upload
          </TabsTrigger>
        </TabsList>

        <TabsContent value="quick" className="space-y-6">
          <QuickCaptureMode />
        </TabsContent>

        <TabsContent value="automated" className="space-y-6">
          <Card className="p-6">
            <h2 className="text-2xl font-bold mb-6">Automated Dickinson County Scraping (Firecrawl)</h2>
            
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">How This Works:</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-200">
                  <li>Firecrawl logs into Dickinson County site automatically</li>
                  <li>Performs search for the date range you specify</li>
                  <li>Captures screenshot of search results</li>
                  <li>AI extracts all records from the screenshot</li>
                  <li>Saves records to County Research database</li>
                  <li>You can then manually upload individual documents for detailed extraction</li>
                </ol>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date (MM/DD/YYYY)</Label>
                  <Input
                    type="text"
                    value={autoStartDate}
                    onChange={(e) => setAutoStartDate(e.target.value)}
                    placeholder="01/01/2025"
                  />
                </div>

                <div className="space-y-2">
                  <Label>End Date (MM/DD/YYYY)</Label>
                  <Input
                    type="text"
                    value={autoEndDate}
                    onChange={(e) => setAutoEndDate(e.target.value)}
                    placeholder="03/31/2025"
                  />
                </div>
              </div>

              <Button
                onClick={handleAutoScrape}
                disabled={isAutoScraping || !autoStartDate || !autoEndDate}
                className="w-full"
              >
                {isAutoScraping ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Scraping with Firecrawl...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Start Automated Scraping
                  </>
                )}
              </Button>

              {autoResults && (
                <Card className="p-4 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <h3 className="font-semibold text-green-900 dark:text-green-100">
                      Scraping Complete!
                    </h3>
                  </div>
                  <div className="text-sm text-green-800 dark:text-green-200 space-y-1">
                    <p><strong>Records Found:</strong> {autoResults.recordsFound}</p>
                    <p><strong>Records Inserted:</strong> {autoResults.recordsInserted}</p>
                    <p className="mt-2 text-xs">Note: Use the Manual Upload tab to extract detailed seller addresses from individual documents.</p>
                  </div>
                </Card>
              )}
            </div>
          </Card>

          <Card className="p-6 bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
            <h3 className="text-lg font-semibold mb-2 text-amber-900 dark:text-amber-100">
              Important Notes
            </h3>
            <ul className="space-y-1 list-disc list-inside text-sm text-amber-800 dark:text-amber-200">
              <li>Process 3-6 months at a time for best results</li>
              <li>This captures the list of records, not individual contract details</li>
              <li>After automated scraping, manually upload screenshots of specific contracts to extract seller addresses</li>
              <li>Takes about 30-60 seconds per search</li>
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="manual" className="space-y-6">
      <Card className="p-6">
        <h2 className="text-2xl font-bold mb-6">Dickinson County Michigan - Manual Land Contract Extraction</h2>
        
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">How to Use This Tool:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-200">
              <li>Login to <a href="https://rod.dickinsoncountymi.gov/Landweb.dll/$/" target="_blank" rel="noopener noreferrer" className="underline">Dickinson County Records</a> (credentials stored securely in password manager)</li>
              <li>Search for land contracts in your desired date range</li>
              <li>Click to view each document image</li>
              <li>Take a screenshot of the document</li>
              <li>Upload the screenshot here - AI will extract all the data automatically</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Document Number (Optional)</Label>
              <Input
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                placeholder="e.g., 2025-001234"
              />
            </div>

            <div className="space-y-2">
              <Label>Filing Date (Optional)</Label>
              <Input
                type="date"
                value={filingDate}
                onChange={(e) => setFilingDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Document Type</Label>
            <Input
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              placeholder="Land Contract"
            />
          </div>

          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this contract..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Upload Contract Screenshot</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
              className="w-full"
              variant="outline"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing with AI...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Choose Image File
                </>
              )}
            </Button>
          </div>

          {processedRecords.length > 0 && (
            <Card className="p-4 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h3 className="font-semibold text-green-900 dark:text-green-100">
                  Processed {processedRecords.length} Contract{processedRecords.length > 1 ? 's' : ''}
                </h3>
              </div>
              <div className="space-y-2">
                {processedRecords.map((record) => (
                  <div key={record.id} className="text-sm text-green-800 dark:text-green-200">
                    <p><strong>Doc #:</strong> {record.document_number}</p>
                    <p><strong>Seller:</strong> {record.seller_name}</p>
                    <p><strong>Address:</strong> {record.seller_address || 'Not extracted'}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">What Gets Extracted</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
          <ul className="space-y-1 list-disc list-inside">
            <li>Seller/Grantor name & address</li>
            <li>Buyer/Grantee name & address</li>
            <li>Property address</li>
            <li>Loan amount</li>
            <li>Interest rate</li>
            <li>Monthly payment</li>
          </ul>
          <ul className="space-y-1 list-disc list-inside">
            <li>Term in months</li>
            <li>Balloon payment details</li>
            <li>Filing date</li>
            <li>Legal description</li>
            <li>Document type</li>
            <li>All extracted data saved automatically</li>
          </ul>
        </div>
      </Card>

      <Card className="p-6 bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
        <h3 className="text-lg font-semibold mb-2 text-amber-900 dark:text-amber-100">
          Tips for Best Results
        </h3>
        <ul className="space-y-1 list-disc list-inside text-sm text-amber-800 dark:text-amber-200">
          <li>Capture the entire document in your screenshot</li>
          <li>Make sure text is clear and readable</li>
          <li>Include all pages if it's a multi-page document</li>
          <li>The AI will extract seller addresses with confidence scoring</li>
          <li>Results below 80% confidence are flagged for manual review</li>
        </ul>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
